import { DOWNHOLE_SOURCES } from './downholeRegistry';

/**
 * Detect within-type overlaps for a single hole and assign sub-channels.
 * Returns the max concurrency (number of distinct sub-types that overlap at
 * any single depth), which determines how many channels this type needs.
 */
function detectWithinTypeOverlaps(intervals, subTypeField) {
  if (!subTypeField || intervals.length === 0) return { maxOverlap: 1, assignments: intervals.map(() => 0) };

  const sorted = intervals
    .map((d, idx) => ({
      from: parseFloat(d.from_depth),
      to: parseFloat(d.to_depth),
      subType: d[subTypeField] || '__unknown__',
      origIdx: idx,
    }))
    .filter(d => !isNaN(d.from) && !isNaN(d.to))
    .sort((a, b) => a.from - b.from || a.to - b.to);

  const subTypeToChannel = new Map();
  let nextChannel = 0;
  const assignments = new Array(intervals.length).fill(0);

  const active = [];

  for (const item of sorted) {
    // Remove intervals that ended before this one starts
    for (let j = active.length - 1; j >= 0; j--) {
      if (active[j].to <= item.from) active.splice(j, 1);
    }

    if (!subTypeToChannel.has(item.subType)) {
      subTypeToChannel.set(item.subType, nextChannel++);
    }
    assignments[item.origIdx] = subTypeToChannel.get(item.subType);

    active.push(item);
  }

  return { maxOverlap: subTypeToChannel.size, assignments };
}

/**
 * Build a flat list of channel-assigned intervals across all active types and holes.
 *
 * Each entry: { interval, sourceId, channelIndex, totalChannels, holeId }
 *
 * @param {Map<string, object[]>} intervalDataMap - Map from sourceId to interval arrays
 * @param {string[]} activeTypes - Active source IDs
 * @returns {object[]} Flat list of channel-assigned intervals
 */
export function assignChannels(intervalDataMap, activeTypes) {
  if (!intervalDataMap || activeTypes.length === 0) return [];

  const allHoleIds = new Set();
  for (const [, intervals] of intervalDataMap) {
    for (const d of intervals) allHoleIds.add(d.hole_id);
  }

  const result = [];

  for (const holeId of allHoleIds) {
    let globalChannelOffset = 0;
    const channelCounts = [];

    for (const sourceId of activeTypes) {
      const src = DOWNHOLE_SOURCES[sourceId];
      const intervals = (intervalDataMap.get(sourceId) || []).filter(d => d.hole_id === holeId);
      if (intervals.length === 0) {
        channelCounts.push(0);
        continue;
      }

      const { maxOverlap, assignments } = detectWithinTypeOverlaps(intervals, src.subTypeField);
      channelCounts.push(maxOverlap);

      for (let i = 0; i < intervals.length; i++) {
        result.push({
          interval: intervals[i],
          sourceId,
          localChannel: assignments[i],
          baseOffset: globalChannelOffset,
          holeId,
        });
      }

      globalChannelOffset += maxOverlap;
    }

    const totalChannels = globalChannelOffset;

    for (const entry of result) {
      if (entry.holeId === holeId && entry.totalChannels === undefined) {
        entry.channelIndex = entry.baseOffset + entry.localChannel;
        entry.totalChannels = totalChannels;
      }
    }
  }

  return result;
}
